/* Estado de la sesión en el navegador. Lo que persiste aquí son preferencias de
   este navegador (tema, último correo, splash visto). Todo lo demás vive en la
   API: la web no es fuente de verdad de nada. */
import type { Me, Tenant, Memoria, ProyectoFav } from "./api";

/* Tres temas (tokens.css): papel, noche y la marca de la demo. El siguiente de
   cada uno, para el botón que los rota. */
export type Tema = "aimak" | "noche" | "redbull";
export const TEMAS: Tema[] = ["aimak", "noche", "redbull"];
export const NOMBRE_TEMA: Record<Tema, string> = { aimak: "Papel", noche: "Noche", redbull: "Red Bull" };
export const temaSiguiente = (t: Tema): Tema => TEMAS[(TEMAS.indexOf(t) + 1) % TEMAS.length]!;
const KEY = "aimak.web.v1";

/* `panel` es cómo quedó el menú lateral en ESTE navegador: completo o plegado a
   iconos. Es preferencia de pantalla, no del usuario, así que vive aquí y no en
   la API — la misma persona quiere el panel plegado en el portátil chico y
   abierto en el monitor grande. */
type Persistido = { tema: Tema; idioma: "es" | "en"; ultimoEmail: string; splashVisto: Record<string, boolean>; panel: "completo" | "iconos"; vistaProyectos: VistaProyectos };
/* Cómo mira los proyectos ESTE navegador. Es de la pantalla, no de la persona
   —el tablero pide ancho—, así que vive aquí. Los favoritos, que sí son de la
   persona, viven en la base (migración 0020). */
export type VistaProyectos = "lista" | "tarjetas" | "tablero";

function cargar(): Persistido {
  const base: Persistido = { tema: "aimak", idioma: "es", ultimoEmail: "", splashVisto: {}, panel: "completo", vistaProyectos: "tarjetas" };
  try { const raw = localStorage.getItem(KEY); return raw ? { ...base, ...JSON.parse(raw) } : base; } catch { return base; }
}

export const estado = {
  me: null as Me | null,
  tenant: null as Tenant | null,
  memoria: null as Memoria | null,
  /* Los atajos de esta sesión, para el panel lateral. Los trae la API en cada
     render y se refrescan al marcar sin recargar la pantalla. */
  favoritos: [] as ProyectoFav[],
  consulta: null as string | null,   // pregunta escrita en la barra, para abrir Makai con ella
  pref: cargar(),
  guardar() { try { localStorage.setItem(KEY, JSON.stringify(this.pref)); } catch { /* sin persistencia */ } },
  aplicarTema() {
    if (!TEMAS.includes(this.pref.tema)) this.pref.tema = "aimak";   // una preferencia vieja que ya no existe
    document.documentElement.className = "tema-" + this.pref.tema;
  },
  usuario() { return this.me?.usuario ?? null; },
  accesos() { return this.me?.accesos ?? { modulos: [], clientes: [], dinero: false, firma: false, auditoria: false, admin: false, acciones: [], firmaTipos: [] }; },
  tieneModulo(m: string) { return (this.accesos().modulos as string[]).includes(m); },
  /* Las dos dimensiones, igual que en la API (app.puede / db.ts:puede). La web
     esconde lo que no se puede hacer; el candado de verdad está en la API. */
  puede(modulo: string, accion: string) {
    const a = this.accesos();
    return a.admin || (this.tieneModulo(modulo) && a.acciones.includes(modulo + ":" + accion));
  },
  /* El dinero se rige por rol, no por pertenencia: no exige el módulo. */
  puedeConDinero(accion: string) {
    const a = this.accesos();
    return a.admin || (a.dinero && a.acciones.includes("finanzas:" + accion));
  },
  puedeAuditoria(accion: "ver" | "exportar") {
    const a = this.accesos();
    return a.admin || (a.auditoria && a.acciones.includes("auditoria:" + accion));
  },
  /* Firmar: permiso propio + el tipo de contenido que firma su rol. */
  puedeFirmar(tipoTareaId?: string) {
    const a = this.accesos();
    return a.admin || (a.firma && !!tipoTareaId && a.firmaTipos.includes(tipoTareaId));
  },
  tieneCliente(id: string) { return this.accesos().clientes.some((c) => c.id === id); },
  nombreCliente(id: string) { return this.accesos().clientes.find((c) => c.id === id)?.nombre ?? "—"; },
  colorCliente(id: string) { return this.accesos().clientes.find((c) => c.id === id)?.color ?? "var(--border3)"; },
};
